import { CustomReportComponent } from './custom-report/custom-report.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpService, ModalService } from 'app/services';
import { ReportGenerator } from './report-generator';
import { MatDialog } from '@angular/material/dialog';
import { audit, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

export type ReportSettings = {
  recipients: string[];
  customers?: string[];
  locations?: string[];
  unitAmount?: number;
  timeOfDay?: string;
  filters: string[];
  unitType: number;
  times?: string[];
  level: number;
  name: string;
}

export type ScheduledReport = {
  deployment_summary_report?: boolean;
  device_alert_report?: boolean;
  settings: ReportSettings;
  next_report: number;
  begin_date?: number;
  interval?: number;
  enabled: boolean;
  id?: string;
}

export type OptionObject = {
  id: string,
  name: string,
  isUser?: boolean
}

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit, OnDestroy {
  private urlParams: any = new URLSearchParams(window.location.search);
  private reportGenerator: (ReportGenerator | null) = null;
  private refreshInterval: (NodeJS.Timeout | undefined);
  private recipients: OptionObject[] = [];
  private customers: OptionObject[] = [];
  private locations: OptionObject[] = [];

  public dayOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  public level = ['customers', 'locations', 'business', 'custom', 'Permanent'];
  public unitType = ['Day(s)', 'Week(s)', 'Month(s)'];

  public tabIndex: number = parseInt(this.urlParams.get('tab') || '0');
  public reportIndex: number = (this.urlParams.get('report') || '0');
  public includeServiceMode: boolean = false;
  public showLoader: boolean = false;
  public streamline: boolean = false;
  public scheduledReports: ScheduledReport[] = [
    {
      next_report: 0,
      enabled: false,
      settings: {
        name: 'Deployment Overview',
        times: ['', '', '1'],
        recipients: [],
        unitType: 5,
        filters: [],
        level: 4,
      }
    },
    {
      next_report: 0,
      enabled: false,
      settings: {
        name: 'Device Alerts',
        times: ['', '', '1'],
        recipients: [],
        unitType: 5,
        filters: [],
        level: 4,
      }
    }
  ];

  constructor(
    private modalService: ModalService,
    private snackbar: MatSnackBar,
    private matDialog: MatDialog,
    private http: HttpService,
    private router: Router
  ) { }

  private async setUrlParameters(value: { [key: string]: any }) {
    const currentParams: any = new URLSearchParams(window.location.search);
    const queryParams = { ...Object.fromEntries(currentParams), ...value };

    await this.router.navigate([], { queryParams, replaceUrl: true });
  }

  private getOrdinalDay(day: number) {
    const k = day % 100;
    if (k >= 11 && k <= 13) return `${day}th`;

    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  }

  private async refresh() {
    const businessData: any = await this.http.getBusiness({
      fields: ['id', 'email', 'name'],
      include: [
        { relation: 'scheduledReports' },
        {
          relation: 'users',
          scope: {
            fields: ['id', 'business_id', 'email']
          }
        },
        {
          relation: 'notificationPoints',
          scope: {
            fields: ['id', 'business_id', 'email']
          }
        },
        {
          relation: 'businessAccounts',
          scope: {
            fields: ['id', 'business_id', 'contact_email', 'name'],
            include: [{
              relation: 'locations',
              scope: {
                fields: ['id', 'bus_sub_id', 'contact_email', 'name']
              }
            }]
          }
        }
      ]
    });

    if (!businessData) return;

    let _recipients: OptionObject[] = [];
    let _customers: OptionObject[] = [];
    let _locations: OptionObject[] = [];

    _recipients.push({ id: businessData.email, name: businessData.email });
    for (const cus of (businessData.businessAccounts || [])) {
      if (!_recipients.find(r => r.id === cus.contact_email)) _recipients.push({ id: cus.contact_email, name: cus.contact_email });
      _customers.push({ id: cus.id, name: cus.name });

      for (const loc of (cus.locations || [])) {
        if (!_recipients.includes(loc.contact_email)) _recipients.push({ id: loc.contact_email, name: loc.contact_email });
        _locations.push({ id: loc.id, name: loc.name });
      }
    }

    for (const item of [...(businessData.notificationPoints || [])]) {
      if (!_recipients.find(r => r.id === item.email)) _recipients.push({ id: item.email, name: item.email });
    }

    for (const item of [...(businessData.users || [])]) {
      if (!_recipients.find(r => r.id === item.id)) _recipients.push({ id: item.id, name: item.email, isUser: true });
    }

    this.recipients = _recipients.filter(i => i.id);
    this.customers = _customers;
    this.locations = _locations;

    if (this.tabIndex === 1) {
      let _reports = [this.scheduledReports[0], this.scheduledReports[1]];
      for (const report of [...(businessData.scheduledReports || [])]) {
        if (report.settings) {
          report.settings = JSON.parse(report.settings);
          _reports.push(report);
        } else {
          const date = new Date(report.begin_date);
          const [dateString, fullTime] = date.toISOString().split('T');
          const timeString = fullTime.slice(0, 5);

          _reports[0].settings.times = [dateString, timeString, report.interval.toString()];
          _reports[0].settings.recipients = report.users;
          _reports[0].next_report = report.next_report;
          _reports[0].id = report.id;

          _reports[1].settings.times = [dateString, timeString, report.interval.toString()];
          _reports[1].settings.recipients = report.users;
          _reports[1].next_report = report.next_report;
          _reports[1].id = report.id;

          _reports[0].enabled = report.deployment_summary_report;
          _reports[1].enabled = report.device_alert_report;
        }
      }

      this.scheduledReports = _reports;
    } else {
      if (!this.reportGenerator) {
        this.reportGenerator = new ReportGenerator(this.modalService, this.http, businessData.name, this.includeServiceMode, (() => {
          this.showLoader = false;
        }).bind(this));
      }
    }
  }

  private startRefreshInterval() {
    if (this.refreshInterval) return;
    this.refreshInterval = setInterval(this.refresh.bind(this), 30000);
  }

  private stopRefreshInterval() {
    if (!this.refreshInterval) return;

    clearInterval(this.refreshInterval);
    this.refreshInterval = undefined;
  }

  private saveReportEnabled(report: ScheduledReport) {
    return new Promise((res) => {
      let data: any = {};

      if (report.settings.level !== 4) data.enabled = report.enabled;
      else {
        if (report.settings.name.includes('Alerts')) data.device_alert_report = report.enabled;
        else data.deployment_summary_report = report.enabled;
      }

      this.http.updateScheduledReports(data, (report?.id as string)).pipe(
        catchError((err: any, msg?: any): any => {
          this.modalService.errorDialog(err, msg);
          res(false);
        })
      ).subscribe(() => {
        this.snackbar.open('Scheduled report configuration saved', 'OK', { duration: 2000 });
        res(true);
      });
    });
  }

  private untetherObject(obj: Object) {
    return JSON.parse(JSON.stringify(obj));
  }

  ngOnDestroy(): void {
    this.stopRefreshInterval();
  }

  ngOnInit(): void {
    this.refresh().then(() => {
      if (this.urlParams.get('audit')) {
        let auditDate = new Date(Date.now() - (7 * 86400000)); //set to 7 days before
        auditDate.setHours(0, 0, 0, 0);
        this.reportIndex = 2;

        this.setUrlParameters({ report: 2 }).then(() => {
          (this.reportGenerator as ReportGenerator).reports[2].filterProps = {
            locations: [this.urlParams.get('audit')],
            beginDate: auditDate
          };

          this.generateReport();
        });
      } else if (this.tabIndex == 0) this.generateReport();

      if (this.tabIndex == 1) this.startRefreshInterval();
    });
  }

  deleteReport(reportIndex: number) {
    const report = { ...this.scheduledReports[reportIndex] };
    if (report?.settings?.level === 4 || !report?.id) return;

    this.scheduledReports.splice(reportIndex, 1);
    this.http.deleteScheduledReports(report.id as string).pipe(
      catchError(this.modalService.errorDialog)
    ).subscribe(() => {
      this.snackbar.open('Scheduled report deleted', 'OK', { duration: 2000 });
      this.refresh();
    });
  }

  toggleEnableReport(report: ScheduledReport, all?: boolean) {
    if (!report?.id) return;

    report.enabled = !report.enabled;
    this.saveReportEnabled(report).then((res) => {
      if (res && !all) this.refresh();
    });
  }

  disableAllReports() {
    Promise.all(this.scheduledReports.map((report) => {
      if (!report?.enabled || !report?.id) return;

      report.enabled = false;
      return this.saveReportEnabled(report);
    })).then((res) => {
      if (res.find(i => i === true)) this.refresh();
    });
  }

  reportInterval(report: ScheduledReport) {
    let out = `@${report.settings.timeOfDay} x `;
    if (report.settings.level === 4) {
      return `@${report.settings.times?.[1] || '01:00'} x ${report.settings.times?.[2]} Day(s)`;
    }

    switch (report.settings.unitType) {
      case 0:
      case 1:
      case 2:
        out += `${report.settings.unitAmount} ${this.unitType[0]}`;
        break;
      case 3:
        out += this.dayOfWeek[report.settings.unitAmount as number];
        break;
      case 4:
        out += this.getOrdinalDay(report.settings.unitAmount as number);
        break;
      case 5:
        out = `@Multiple x Daily`;
        break;
    }

    return out;
  }

  toLocalTime(ms: number) {
    return new Date(ms).toLocaleString();
  }

  createReport(report?: ScheduledReport) {
    this.stopRefreshInterval();

    let _recipients = (this.untetherObject(this.recipients) as OptionObject[]);
    if (report?.settings.level === 4) _recipients = _recipients.filter(r => r.isUser);
    else {
      _recipients.map((r) => {
        if (r.isUser) r.id = r.name;
      });
    }

    const container = document.getElementsByTagName('content')[0];
    const dialog = this.matDialog.open(CustomReportComponent, {
      minWidth: '25vw',
      data: {
        names: this.scheduledReports.map(r => r.settings.name).filter(n => n !== ''),
        title: (report ? 'Edit Scheduled Report' : 'Create Scheduled Report'),
        settings: report?.settings,
        customers: this.customers,
        locations: this.locations,
        recipients: _recipients,
        relativeTo: container
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (!result?.name) return;

      if (report) report.settings = result;
      else this.scheduledReports.push({
        settings: result,
        next_report: 0,
        enabled: false
      });

      let id = report?.id;
      let type = 'create';
      let data: any = {
        deployment_summary_report: false,
        device_alert_report: false,
        next_report: 0,
        begin_date: 0,
        interval: 0
      };

      if (result.level !== 4) data.settings = JSON.stringify(result);
      else {
        delete data.deployment_summary_report;
        delete data.device_alert_report;

        data.begin_date = new Date(`${result.times[0]}T${result.times[1]}Z`).getTime();
        data.interval = Number(result.times[2]);
        data.users = result.recipients;

        if (!id) {
          const overview = this.scheduledReports.find(r => r.deployment_summary_report);
          const alerts = this.scheduledReports.find(r => r.device_alert_report);

          if (result.name.includes('Alerts') && overview) id = overview.id;
          else if (result.name.includes('Overview') && alerts) id = alerts.id;
        }
      }

      if (id) type = 'update';
      else data.enabled = false;

      (this.http as any)[`${type}ScheduledReports`](data, id).pipe(
        catchError(this.modalService.errorDialog)
      ).subscribe(() => {
        this.snackbar.open('Scheduled report configuration saved', 'OK', { duration: 2000 });
        this.refresh();
      });

      this.startRefreshInterval();
    });
  }

  async changeTab(event: MatTabChangeEvent) {
    this.setUrlParameters({ tab: event.index, audit: undefined });
    this.tabIndex = event.index;
    await this.refresh();

    if (this.tabIndex == 0) {
      if ((this.reportGenerator as ReportGenerator).includeServiceMode !== this.includeServiceMode) {
        (this.reportGenerator as ReportGenerator).toggleServiceMode();
      }

      this.generateReport();
    }
  }

  toggleServiceMode() {
    this.includeServiceMode = !this.includeServiceMode;
    if (this.reportGenerator && this.tabIndex == 0) (this.reportGenerator as ReportGenerator).toggleServiceMode();
  }

  reportChange(event: MouseEvent) {
    const target = (event.target as HTMLElement);
    const parent = target.parentElement;

    if (target.nodeName !== 'SPAN' && !parent) return;
    if (parent) {
      const targetIndex = Array.from(parent.children).indexOf(target);
      if (targetIndex == this.reportIndex) return;

      this.setUrlParameters({ report: targetIndex, audit: undefined });
      this.reportIndex = targetIndex;
      this.generateReport();
    }
  }

  async share() {
    await (this.reportGenerator as ReportGenerator).shareReport();
  }

  async exportCSV() {
    await (this.reportGenerator as ReportGenerator).exportCsv();
  }

  filter() {
    (this.reportGenerator as ReportGenerator).customizeReport();
  }

  private generateReport() {
    this.showLoader = true;
    const report = (this.reportGenerator as ReportGenerator).reports[this.reportIndex];

    if (!report) {
      console.error('Report not found for tab:', report.textLabel);
      return;
    }

    (this.reportGenerator as ReportGenerator).activeReport = report;
  }
}